import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../shared/HubLayout.css';
import { useMobileSubNav } from '../layout/MobileNavContext';
import { useHubScreen } from '../layout/useHubScreen';
import BackToLotLink from '../shared/lots/BackToLotLink';
import Upload from '../shared/Upload.js';
import WeightLots, { WeightLotsActions } from './lots/WeightLots.js';
import WeightAnimals, { WeightAnimalsActions } from './animals/WeightAnimals.js';
import WeightSlaughter, { WeightSlaughterActions } from './WeightSlaughter.js';
import WeightProtocolSettings from './WeightProtocolSettings.js';
import Protocols, { ProtocolsActions } from '../reproduction/protocols/Protocols';
import SectionDescription from '../shared/SectionDescription';

const SUB_PATHS = ['animals', 'lots', 'slaughter', 'plans', 'settings'];

const SECTION_INFO = [
  { titleKey: 'weight_animals_section_title', textKey: 'weight_animals_section_text' },
  { titleKey: 'weight_lots_section_title',    textKey: 'weight_lots_section_text' },
  { titleKey: 'slaughter_section_title',      textKey: 'slaughter_section_text' },
  { titleKey: 'weight_plans_section_title',   textKey: 'weight_plans_section_text' },
  { titleKey: 'weight_settings_section_title', textKey: 'weight_settings_section_text' },
];

// Weight data model for upload
const weightDataModel = [
  { field: 'tag',    type: 'number', descriptionKey: 'upload_desc_weight_tag',  range: [0, 99999999], required: true },
  { field: 'weight', type: 'number', descriptionKey: 'upload_desc_weight_kg',   range: [1, 9999],     required: true },
  { field: 'date',   type: 'date',   descriptionKey: 'upload_desc_weight_date', required: true },
];

export const Weight = () => {
  const { t } = useTranslation();
  const { subTab, screen, setScreen, recordId } = useHubScreen();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { setSubNav } = useMobileSubNav();

  const activeSubTab = Math.max(0, SUB_PATHS.indexOf(subTab));

  const [lotFilter, setLotFilter] = useState(location.state?.lotFilter ?? null);

  const subTabs = [
    t('animals'),
    t('weight_lots'),
    t('slaughter_tab'),
    t('plans'),
    t('settings'),
  ];

  const handleSubTabClick = (index) => {
    if (index !== 0) setLotFilter(null);
    navigate(`/weight/${SUB_PATHS[index]}`);
  };

  useEffect(() => {
    setSubNav({ tabs: subTabs, activeTab: activeSubTab, onTabClicked: handleSubTabClick });
    return () => setSubNav(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, setSubNav]);

  return (
    <div className="hub">
      <div className="hub-content">
        {activeSubTab === 0 && (
          <BackToLotLink lotId={lotFilter} prefix={t('weight_lot_prefix')} basePath="/weight/lots" />
        )}
        <SectionDescription
          title={t(SECTION_INFO[activeSubTab].titleKey)}
          text={t(SECTION_INFO[activeSubTab].textKey)}
        />

        {activeSubTab === 0 && screen !== 'upload' && (
          <WeightAnimals
            initialLotFilter={lotFilter}
            screen={screen}
            setScreen={setScreen}
            actions={<WeightAnimalsActions screen={screen} setScreen={setScreen} />}
          />
        )}
        {activeSubTab === 0 && screen === 'upload' && (
          <Upload collection="weights" dataModel={weightDataModel} onSuccess={() => setScreen('list')} />
        )}

        {activeSubTab === 1 && (
          <WeightLots
            screen={screen}
            setScreen={setScreen}
            actions={<WeightLotsActions screen={screen} setScreen={setScreen} />}
          />
        )}

        {activeSubTab === 2 && (
          <WeightSlaughter
            screen={screen}
            setScreen={setScreen}
            actions={<WeightSlaughterActions screen={screen} setScreen={setScreen} />}
          />
        )}

        {activeSubTab === 3 && (
          <Protocols
            planType="weight"
            recordId={recordId}
            screen={screen}
            setScreen={setScreen}
            actions={<ProtocolsActions screen={screen} setScreen={setScreen} />}
          />
        )}

        {activeSubTab === 4 && <WeightProtocolSettings />}
      </div>
    </div>
  );
};

export default Weight;
